/**
 * Google Analytics 4 Tracking Utilities
 *
 * Provides type-safe event tracking for all user interactions.
 * GA4 Measurement ID: G-7KCV2MF029
 */

// Declare gtag on window
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
  }
}

/**
 * Check if analytics is available
 */
const isAnalyticsAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Which surface the app is running on. GA4's built-in Device Category can't
 * tell an installed app from a mobile browser — a TWA is Chrome under the hood
 * and reports as mobile web — so we compute this ourselves and attach it to
 * every event (and as a user property).
 *   - 'twa'         Android app (Trusted Web Activity)
 *   - 'pwa'         installed PWA / iOS home-screen app
 *   - 'mobile_web'  mobile browser tab
 *   - 'desktop_web' desktop browser tab
 */
export type AppSurface = 'twa' | 'pwa' | 'mobile_web' | 'desktop_web';

let _surface: AppSurface | null = null;
let _surfacePropSet = false;

export const detectSurface = (): AppSurface => {
  if (_surface) return _surface;
  if (typeof window === 'undefined') return 'desktop_web';

  // TWA: the Android wrapper appends ?is_twa=true (persisted to localStorage);
  // Chrome also sets an android-app:// referrer on TWA launches.
  let isTwa = false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('is_twa') === 'true') localStorage.setItem('is_twa', 'true');
    isTwa =
      localStorage.getItem('is_twa') === 'true' ||
      document.referrer.startsWith('android-app://');
  } catch { /* private mode / no storage */ }

  const standalone =
    (window.matchMedia && (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches
    )) ||
    (navigator as unknown as { standalone?: boolean }).standalone === true; // iOS

  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  _surface = isTwa ? 'twa' : standalone ? 'pwa' : isMobile ? 'mobile_web' : 'desktop_web';
  return _surface;
};

/**
 * Track a custom event. Every event automatically carries `surface` so any
 * report or funnel can be broken down by app-vs-mobile-web-vs-desktop.
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean>
): void => {
  if (!isAnalyticsAvailable()) return;

  const surface = detectSurface();
  // Set the user-scoped `surface` property once, the first time analytics is
  // actually live (robust to consent loading gtag after boot).
  if (!_surfacePropSet) {
    window.gtag!('set', 'user_properties', { surface });
    _surfacePropSet = true;
  }
  window.gtag!('event', eventName, { surface, ...params });
};

// ============================================================================
// VIEW & NAVIGATION EVENTS
// ============================================================================

/**
 * Track view mode changes (2D/3D)
 */
export const trackViewModeChange = (mode: '2d' | '3d', source?: string): void => {
  trackEvent('view_mode_change', {
    view_mode: mode,
    source: source || 'unknown',
  });
};

/**
 * Track mobile view toggle (map/list)
 */
export const trackMobileViewToggle = (view: 'map' | 'list'): void => {
  trackEvent('mobile_view_toggle', {
    mobile_view: view,
  });
};

/**
 * Track modal opens
 */
export const trackModalOpen = (
  modalName:
    | 'auth'
    | 'admin'
    | 'profile'
    | 'about'
    | 'onboarding'
    | 'settings'
    | 'notifications'
    | 'radio_scanner'
    | 'welcome_3d'
    | 'disclaimer'
): void => {
  trackEvent('modal_open', {
    modal_name: modalName,
  });
};

/**
 * Track modal closes
 */
export const trackModalClose = (modalName: string, action?: string): void => {
  trackEvent('modal_close', {
    modal_name: modalName,
    action: action || 'dismiss',
  });
};

// ============================================================================
// FIRE EVENT INTERACTIONS
// ============================================================================

/**
 * Track fire event selection
 */
export const trackFireEventSelect = (
  eventId: string,
  eventName: string,
  alertLevel: string,
  region: string,
  source: 'map' | 'list' | 'timeline' | 'notification'
): void => {
  trackEvent('fire_event_select', {
    event_id: eventId,
    event_name: eventName.substring(0, 100), // Limit length
    alert_level: alertLevel,
    region: region,
    selection_source: source,
  });
};

/**
 * Track fire detail panel section expansion
 */
export const trackDetailPanelExpand = (
  section: 'wind' | 'forecast' | 'comments' | 'social' | 'photos' | 'ember_risk'
): void => {
  trackEvent('detail_panel_expand', {
    section: section,
  });
};

/**
 * Track View in 3D button click
 */
export const trackViewIn3D = (eventId: string, eventName: string): void => {
  trackEvent('view_in_3d', {
    event_id: eventId,
    event_name: eventName.substring(0, 100),
  });
};

/**
 * Track event list filter change
 */
export const trackEventListFilter = (filterType: string): void => {
  trackEvent('event_list_filter', {
    filter_type: filterType,
  });
};

/**
 * Track event list view mode (list/timeline)
 */
export const trackEventListViewMode = (mode: 'list' | 'timeline'): void => {
  trackEvent('event_list_view_mode', {
    view_mode: mode,
  });
};

// ============================================================================
// MAP LAYER INTERACTIONS
// ============================================================================

/**
 * Track layer visibility toggle
 */
export const trackLayerToggle = (
  layerName: string,
  visible: boolean
): void => {
  trackEvent('layer_toggle', {
    layer_name: layerName,
    layer_visible: visible,
  });
};

/**
 * Layer names for tracking reference
 */
export const TRACKED_LAYERS = {
  fire_boundaries: 'Fire Boundaries',
  warning_areas: 'Warning Areas',
  satellite_hotspots: 'Satellite Hotspots',
  road_closures: 'Road Closures',
  power_outages: 'Power Outages',
  wind_animation: 'Wind Animation',
  aircraft_tracking: 'Aircraft Tracking',
  smoke_nasa: 'Smoke (NASA)',
  air_quality: 'Air Quality',
  city_temperatures: 'City Temperatures',
  community_photos: 'Community Photos',
  thunderstorm_warnings: 'Thunderstorm Warnings',
  dry_lightning_risk: 'Dry Lightning Risk',
  live_lightning: 'Live Lightning Strikes',
  ember_attack: 'Ember Attack Zones',
  fdr_zones: 'Fire Danger Ratings',
} as const;

// ============================================================================
// WATCH ZONE INTERACTIONS
// ============================================================================

/**
 * Track watch zone creation
 */
export const trackWatchZoneCreate = (
  type: 'circle' | 'polygon',
  radiusKm?: number
): void => {
  trackEvent('watch_zone_create', {
    zone_type: type,
    radius_km: radiusKm || 0,
  });
};

/**
 * Track watch zone selection
 */
export const trackWatchZoneSelect = (zoneId: string): void => {
  trackEvent('watch_zone_select', {
    zone_id: zoneId,
  });
};

/**
 * Track watch zone deletion
 */
export const trackWatchZoneDelete = (zoneId: string): void => {
  trackEvent('watch_zone_delete', {
    zone_id: zoneId,
  });
};

/**
 * Track watch zone radius change
 */
export const trackWatchZoneRadiusChange = (radiusKm: number): void => {
  trackEvent('watch_zone_radius_change', {
    radius_km: radiusKm,
  });
};

// ============================================================================
// SUPPORTER / MONETIZATION FUNNEL
// ============================================================================

/**
 * Track when the supporter modal is opened, with the `source` gate that
 * triggered it (e.g. 'layer:aircraft_tracking', 'zoom_gate',
 * 'watch_zone_limit', '3d:ember'). This is the top of the conversion funnel —
 * it tells us WHICH gate drives interest.
 */
export const trackSupporterModalOpen = (source: string): void => {
  trackEvent('supporter_modal_open', { source });
};

/**
 * Track when the user starts Stripe checkout (clicked "Start Free Trial"),
 * attributed to the source gate.
 */
export const trackSupporterCheckoutStart = (source: string): void => {
  trackEvent('supporter_checkout_start', { source });
};

/**
 * Track a completed subscription (fired when the user returns from Stripe with
 * ?subscription=success). The bottom of the funnel.
 */
export const trackSupporterSubscribed = (): void => {
  trackEvent('supporter_subscribed');
};

/**
 * Track opening the Stripe billing portal (manage subscription).
 */
export const trackSupporterPortalOpen = (): void => {
  trackEvent('supporter_portal_open');
};

// ============================================================================
// LOCATION & AREA INTERACTIONS
// ============================================================================

/**
 * Track area preset selection
 */
export const trackAreaSelect = (areaName: string): void => {
  trackEvent('area_select', {
    area_name: areaName,
  });
};

/**
 * Track My Location button click
 */
export const trackMyLocationClick = (success: boolean): void => {
  trackEvent('my_location_click', {
    success: success,
  });
};

/**
 * Track location search
 */
export const trackLocationSearch = (query: string): void => {
  trackEvent('location_search', {
    search_query: query.substring(0, 50),
  });
};

// ============================================================================
// USER AUTHENTICATION EVENTS
// ============================================================================

/**
 * Track login attempt
 */
export const trackLoginAttempt = (method: 'email' | 'google' | 'facebook'): void => {
  trackEvent('login_attempt', {
    login_method: method,
  });
};

/**
 * Track login success
 */
export const trackLoginSuccess = (method: string): void => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Track logout
 */
export const trackLogout = (): void => {
  trackEvent('logout');
};

/**
 * Track signup attempt
 */
export const trackSignupAttempt = (): void => {
  trackEvent('sign_up_attempt');
};

/**
 * Track signup success
 */
export const trackSignupSuccess = (): void => {
  trackEvent('sign_up');
};

// ============================================================================
// MEDIA INTERACTIONS
// ============================================================================

/**
 * Track photo upload
 */
export const trackPhotoUpload = (
  hasGps: boolean,
  source: 'camera' | 'gallery'
): void => {
  trackEvent('photo_upload', {
    has_gps: hasGps,
    source: source,
  });
};

/**
 * Track photo view
 */
export const trackPhotoView = (photoId: string): void => {
  trackEvent('photo_view', {
    photo_id: photoId,
  });
};

/**
 * Track comment submission
 */
export const trackCommentSubmit = (targetType: 'event' | 'photo'): void => {
  trackEvent('comment_submit', {
    target_type: targetType,
  });
};

// ============================================================================
// APP ACTIONS
// ============================================================================

/**
 * Track data refresh button click
 */
export const trackDataRefresh = (): void => {
  trackEvent('data_refresh');
};

/**
 * Track radio scanner link click
 */
export const trackRadioScannerClick = (region: string): void => {
  trackEvent('radio_scanner_click', {
    region: region,
  });
};

/**
 * Track official links panel toggle
 */
export const trackOfficialLinksToggle = (expanded: boolean): void => {
  trackEvent('official_links_toggle', {
    expanded: expanded,
  });
};

/**
 * Track external link click
 */
export const trackExternalLinkClick = (
  linkName: string,
  url: string
): void => {
  trackEvent('external_link_click', {
    link_name: linkName,
    link_url: url.substring(0, 100),
  });
};

/**
 * Track share action
 */
export const trackShare = (method: string, contentType: string): void => {
  trackEvent('share', {
    method: method,
    content_type: contentType,
  });
};

// ============================================================================
// ONBOARDING & SAFETY
// ============================================================================

/**
 * Track disclaimer acceptance
 */
export const trackDisclaimerAccept = (): void => {
  trackEvent('disclaimer_accept');
};

/**
 * Track onboarding completion
 */
export const trackOnboardingComplete = (skipped: boolean): void => {
  trackEvent('onboarding_complete', {
    skipped: skipped,
  });
};

/**
 * Track 3D welcome modal completion
 */
export const trackWelcome3DComplete = (dontShowAgain: boolean): void => {
  trackEvent('welcome_3d_complete', {
    dont_show_again: dontShowAgain,
  });
};

/**
 * Track safety banner dismiss
 */
export const trackSafetyBannerDismiss = (): void => {
  trackEvent('safety_banner_dismiss');
};

// ============================================================================
// ERROR TRACKING
// ============================================================================

/**
 * Track application errors
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  componentName?: string
): void => {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 200),
    component: componentName || 'unknown',
  });
};

// ============================================================================
// PERFORMANCE TRACKING
// ============================================================================

/**
 * Track page/view load timing
 */
export const trackTiming = (
  category: string,
  variable: string,
  valueMs: number
): void => {
  trackEvent('timing_complete', {
    timing_category: category,
    timing_variable: variable,
    value: Math.round(valueMs),
  });
};

/**
 * Track API response time
 */
export const trackApiTiming = (
  endpoint: string,
  durationMs: number,
  success: boolean
): void => {
  trackEvent('api_timing', {
    endpoint: endpoint,
    duration_ms: Math.round(durationMs),
    success: success,
  });
};

// ============================================================================
// USER PROPERTIES
// ============================================================================

/**
 * Set user properties for better segmentation
 */
export const setUserProperties = (properties: {
  is_authenticated?: boolean;
  has_watch_zones?: boolean;
  preferred_view?: '2d' | '3d';
  region_preference?: string;
  surface?: AppSurface;
}): void => {
  if (!isAnalyticsAvailable()) return;

  window.gtag!('set', 'user_properties', properties);
};

// ============================================================================
// VIRTUAL PAGE VIEWS (for SPA navigation)
// ============================================================================

/**
 * Track virtual page view for SPA navigation
 */
export const trackPageView = (
  pagePath: string,
  pageTitle: string
): void => {
  if (!isAnalyticsAvailable()) return;

  window.gtag!('config', 'G-7KCV2MF029', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Track screen view (for mobile-like views)
 */
export const trackScreenView = (screenName: string): void => {
  trackEvent('screen_view', {
    screen_name: screenName,
  });
};
